import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import moment from 'moment';

import { PesajeProps } from '../../components/Alimentacion_Pesaje/Pesaje/types';
import { usePets } from '../../context/PetsContext';
import {
  PesajeReq,
  Pesaje as PesajeType,
  getEmptyPesaje,
} from '../../types/Alimentacion-Pesaje/types';
import { today } from '../../utils/date';
import { useEnter } from '../Utils/useEnter';
import { usePostPesaje } from './useAlimentacionPesaje';

const PESO_MIN = 0;
const PESO_MAX = 200;
const HISTORIAL_LIMIT = 15;

export const usePesajeHelper = ({ view, setView, showAlert }: PesajeProps) => {
  const { ref: enterRef, handleEnter } = useEnter();
  const { currentPet, mutate } = usePets();
  const { postPesaje, isLoading } = usePostPesaje();
  const { control, handleSubmit, errors, reset } = useForm<PesajeType>({
    defaultValues: getEmptyPesaje(),
  });

  const [historial, setHistorial] = useState<PesajeType[]>([]);

  const sortedHistorial = useMemo(() => {
    const seen = new Set<string>();
    return [...historial]
      .sort((a, b) => {
        const diff = moment(b.fecha_pesaje).diff(moment(a.fecha_pesaje), 'days');
        return diff !== 0 ? diff : (Number(b.peso) || 0) - (Number(a.peso) || 0);
      })
      .filter((item) => {
        const key = `${moment(item.fecha_pesaje).format('YYYY-MM-DD')}-${item.peso}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, HISTORIAL_LIMIT);
  }, [historial]);

  const historialConVariaciones = useMemo(() => {
    const sorted = [...sortedHistorial].sort((a, b) =>
      moment(b.fecha_pesaje).diff(moment(a.fecha_pesaje)),
    );

    return sorted.map((item, index) => ({
      ...item,
      variacion: sorted[index + 1] ? item.peso - sorted[index + 1].peso : null,
    }));
  }, [sortedHistorial]);

  const onSubmit = async (values: PesajeType) => {
    const peso = Number(values.peso);

    if (peso <= PESO_MIN || peso > PESO_MAX) {
      showAlert({
        header: 'Peso inválido',
        message: `Ingresa un peso entre ${PESO_MIN} y ${PESO_MAX} kg.`,
        buttons: [{ text: 'Entendido', role: 'cancel' }],
      });
      return;
    }

    try {
      const payload: PesajeReq = {
        mascotaId: String(currentPet!.id),
        peso,
        fecha_pesaje: values.fecha_pesaje,
      };

      const success = await postPesaje(payload);

      if (success) {
        // Optimistic update: agregar al estado local inmediatamente
        const nuevoPesaje: PesajeType = {
          peso,
          fecha_pesaje: values.fecha_pesaje,
        };
        setHistorial((prev) => [nuevoPesaje, ...prev]);

        // Sincronizar con el servidor en background
        mutate();

        showAlert({
          header: 'Pesaje agregado',
          message: 'El pesaje se agregó correctamente.',
          buttons: [
            {
              text: 'Entendido',
              handler: () => {
                setView('historial');
                reset();
              },
            },
          ],
        });
      }
    } catch (error) {
      console.error('Error al agregar peso:', error);
      showAlert({
        header: 'Error',
        message: 'No se pudo agregar el peso. Intenta nuevamente.',
        buttons: [{ text: 'Entendido' }],
      });
    }
  };

  useEffect(() => {
    if (currentPet?.historial_peso) {
      setHistorial(
        currentPet.historial_peso.map((item) => ({
          ...item,
          peso: Number(item.peso),
        })),
      );
    }
  }, [currentPet]);

  return {
    isLoading,
    historialConVariaciones,
    today,
    control,
    errors,
    handleSubmit,
    enterRef,
    handleEnter,
    onSubmit,
    handleNavigate: setView,
    formatFecha: (fecha: string) => moment(fecha).format('DD/MM/YY'),
    formatVariacion: (variacion: number) => `${variacion >= 0 ? '+' : ''}${variacion}`,
    getVariacionColor: (variacion: number) => (variacion >= 0 ? 'success' : 'danger'),
    view,
  };
};
